// src/features/links/DualScrollArea.tsx
// IR-1213 — a horizontally-scrollable area with a SECOND scrollbar mirrored at
// the TOP of the content (the native one stays at the bottom). Tomer's Join
// Links table is wider than the viewport; with only a bottom scrollbar you must
// scroll to the end of a long list to reach it. The top bar lets you pan the
// right-most columns (Operational Status / Approval Required / Actions) into view
// without leaving the header.
//
// The top bar is a thin proxy element whose inner spacer is kept exactly as wide
// as the content's scrollWidth, so its thumb size/position match the bottom bar.
// scrollLeft is mirrored both ways; a ResizeObserver re-syncs the spacer when the
// content width changes (column show/hide, row load, window resize).
import { useEffect, useRef } from "react";

/** The scrolling pane's test id — also the handle `scrollPaneToTop` looks for. */
export const SCROLL_PANE_TESTID = "dual-scroll-bottom";

/**
 * IR-1386 r5 (Tomer): "When moving to another page, list should 'jump' from the
 * point where we stopped at the current page to the top desired page. Currently
 * the redirection to new page lands more or less at the same level of the page,
 * not on top."
 *
 * The rows scroll inside THIS pane, not the window — the pane is height-bounded
 * so the table's header can stick (IR-1233 r4). Nothing resets its `scrollTop`
 * when the page changes, so page 4 opened at the same offset page 3 was left
 * at: row 1 of the new page was above the fold and the analyst appeared to land
 * mid-list.
 *
 * Exported as a plain function on the DOM rather than a prop or an effect keyed
 * on `page`, because the reset must fire for a PAGE-BUTTON click only. Match
 * navigation also steps the page (useMatchNav), and there the very next thing
 * that happens is a scroll to the match — an effect would fight it and drop the
 * analyst at the top of a page whose match is 40 rows down.
 *
 * Returns false when there is no pane (nothing rendered yet), so a caller can be
 * tested without a DOM.
 */
export function scrollPaneToTop(root: HTMLElement | null): boolean {
  const pane = root?.querySelector<HTMLElement>(`[data-testid="${SCROLL_PANE_TESTID}"]`);
  if (!pane) return false;
  pane.scrollTop = 0;
  // The page itself may also be scrolled (the filter bar is sticky, the noise
  // notice is not) — bring the table back into view when it isn't, but never
  // move the viewport when it already is. Guarded: jsdom has no layout.
  if (typeof root?.scrollIntoView === "function") root.scrollIntoView({ block: "nearest" });
  return true;
}

interface Props {
  children: React.ReactNode;
}

export function DualScrollArea({ children }: Props) {
  const topRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const spacerRef = useRef<HTMLDivElement>(null);
  // Guard so the two onScroll handlers don't ping-pong each other.
  const syncingRef = useRef(false);

  useEffect(() => {
    const bottom = bottomRef.current;
    const spacer = spacerRef.current;
    if (!bottom || !spacer) return;

    const syncWidth = () => {
      spacer.style.width = `${bottom.scrollWidth}px`;
    };
    syncWidth();

    // Re-measure when the content (table) or the viewport width changes.
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(syncWidth);
    ro.observe(bottom);
    const inner = bottom.firstElementChild;
    if (inner) ro.observe(inner);
    return () => ro.disconnect();
  }, []);

  const mirror = (from: HTMLDivElement | null, to: HTMLDivElement | null) => {
    if (!from || !to || syncingRef.current) return;
    syncingRef.current = true;
    to.scrollLeft = from.scrollLeft;
    // Release on the next frame so the programmatic scroll we just triggered
    // doesn't re-enter through the other element's onScroll.
    requestAnimationFrame(() => {
      syncingRef.current = false;
    });
  };

  return (
    <>
      <div
        ref={topRef}
        data-testid="dual-scroll-top"
        aria-hidden="true"
        className="w-full overflow-x-auto overflow-y-hidden"
        onScroll={() => mirror(topRef.current, bottomRef.current)}
      >
        <div ref={spacerRef} className="h-px" />
      </div>
      <div
        ref={bottomRef}
        data-testid={SCROLL_PANE_TESTID}
        // IR-1233 r4: bound the height so THIS pane is the vertical scroll
        // container — the table's sticky header sticks to the nearest scrolling
        // ancestor, and sticky is inert when the page is what scrolls. The
        // 260px offset ≈ page header + filter bar + top proxy bar chrome.
        // scrollIntoView (match nav) scrolls all ancestor containers, so match
        // navigation keeps working inside the pane.
        className="max-h-[calc(100vh-260px)] w-full overflow-auto"
        onScroll={() => mirror(bottomRef.current, topRef.current)}
      >
        {children}
      </div>
    </>
  );
}
